import { useState } from 'react'

import {
  ReconciliationBankJournalLinesDataTableDocument,
  ReconciliationJournalLinesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  BulkManageFinanceReconciliationReconciliationsInput,
  BulkManageFinanceReconciliationsDocument,
  ReconciliationFromFinancialDocumentSidePanelDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Reconcilable, ReconciliationItem, ReconciliationSource } from './types'

// eslint-disable-next-line factorial/no-unhealthy-enums
enum SourceBalanceTypeEnum {
  Credit = 'credit',
  Debit = 'debit',
}

interface Data {
  journalLineId: string
  journalLineReconciledAmountCents: number
  reconcilableId: string
  reconcilableAmountCents: number
}

const getBalanceType = (source: ReconciliationItem): SourceBalanceTypeEnum => {
  return source.creditCentsToReconcile > 0
    ? SourceBalanceTypeEnum.Credit
    : SourceBalanceTypeEnum.Debit
}

interface Props {
  source?: ReconciliationSource
  sourceReconciliations: ReconciliationItem[]
  onJournalLineFullyReconciled?: () => void
  setShowPartialPendingBankJournalModal: (value: boolean) => void
  setShowPartialPendingNotBankJournalModal: (value: boolean) => void
  setShowSplitAmountsModal: (value: boolean) => void
}

export const useReconciliationForm = ({
  source,
  sourceReconciliations,
  onJournalLineFullyReconciled,
  setShowPartialPendingBankJournalModal,
  setShowPartialPendingNotBankJournalModal,
  setShowSplitAmountsModal,
}: Props) => {
  const { company } = useCurrentSession()
  const [dirtyForm, setDirtyForm] = useState<boolean>(false)
  const [selectedReconciliations, setSelectedReconciliations] = useState<ReconciliationItem[]>([])
  const [reconciliationIdsToRemove, setReconciliationIdsToRemove] = useState<string[]>([])
  const [reconciliationValues, setReconciliationValues] = useState<Data[]>([])

  const newReconciliations = selectedReconciliations.filter(
    (reconciliation) => reconciliation.reconciliationId === undefined
  )

  const [bulkManageFinanceReconciliations] = useMutation(BulkManageFinanceReconciliationsDocument, {
    extractorFn: (mutationData) => mutationData.financeMutations.bulkManageFinanceReconciliation,
    refetchQueries: [
      ReconciliationJournalLinesDataTableDocument,
      ReconciliationBankJournalLinesDataTableDocument,
      ReconciliationFromFinancialDocumentSidePanelDocument,
    ],
  })

  useUnsafeEffect(() => {
    setSelectedReconciliations(sourceReconciliations)
    setDirtyForm(false)
  }, [sourceReconciliations])

  const addReconciliation = (reconciliation: ReconciliationItem) => {
    if (!source) {
      return
    }

    const sourceBalanceType = getBalanceType(source)
    const reconciliationBalanceType = getBalanceType(reconciliation)

    if (sourceBalanceType === reconciliationBalanceType) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.reconciliations.errors.invalid_journal_line_match'),
      })
      return
    }

    if (reconciliation.id && !isSelectedReconciliation(reconciliation.id)) {
      setDirtyForm(true)
      const existingReconciliation = selectedReconciliations.find(
        (r) => r.id === reconciliation.id && r.reconciliationId != undefined
      )
      if (existingReconciliation) {
        const reconciliationsWithoutExisting = removeReconciliation(existingReconciliation)
        const newReconciliation = {
          ...reconciliation,
          debitCentsToReconcile:
            existingReconciliation.debitCentsToReconcile + reconciliation.debitCentsToReconcile,
          creditCentsToReconcile:
            existingReconciliation.creditCentsToReconcile + reconciliation.creditCentsToReconcile,
        }
        setSelectedReconciliations([...reconciliationsWithoutExisting, newReconciliation])
      } else {
        setSelectedReconciliations([...selectedReconciliations, { ...reconciliation }])
      }
    }
  }

  const isSelectedReconciliation = (id: string) => {
    return newReconciliations.some((selectedReconciliation) => selectedReconciliation.id === id)
  }

  const removeReconciliation = (reconciliation: ReconciliationItem) => {
    setDirtyForm(true)
    if (reconciliation.reconciliationId) {
      setReconciliationIdsToRemove([...reconciliationIdsToRemove, reconciliation.reconciliationId])
    }
    const reconciliationsWithoutSelected = selectedReconciliations.filter(
      (selectedReconciliation) => selectedReconciliation.id != reconciliation.id
    )
    setSelectedReconciliations(reconciliationsWithoutSelected)
    return reconciliationsWithoutSelected
  }

  const getBalanceAmount = (reconciliation?: ReconciliationItem) => {
    if (!reconciliation) {
      return 0
    }

    return (
      (reconciliation.debitCentsToReconcile || 0) + (reconciliation.creditCentsToReconcile || 0)
    )
  }

  const savedReconciliations = selectedReconciliations.filter(
    (reconciliation) => reconciliation.reconciliationId != undefined
  )

  const notRemovedReconciliationsAmount = savedReconciliations.reduce(
    (acc, r) =>
      r.reconciliationId &&
      !reconciliationIdsToRemove.includes(r.reconciliationId) &&
      (r.debitCentsToReconcile || r.creditCentsToReconcile)
        ? acc + getBalanceAmount(r)
        : acc,
    0
  )

  const initialAmountPendingToBeReconciled =
    getBalanceAmount(source) - notRemovedReconciliationsAmount

  const amountCentsToBeReconciled = newReconciliations.reduce(
    (acc, reconciliation) => acc + getBalanceAmount(reconciliation),
    0
  )

  const createReconciliationData = (
    reconciliationItem: ReconciliationItem,
    amountCentsToReconcile: number
  ): Data => {
    const journalLineId =
      // eslint-disable-next-line no-type-assertion/no-type-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      source?.type === 'bankAccount' ? source?.id! : reconciliationItem.id

    const reconcilableId =
      // eslint-disable-next-line no-type-assertion/no-type-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      source?.type === 'bankAccount' ? reconciliationItem.id : source?.id!

    return {
      journalLineId: journalLineId,
      journalLineReconciledAmountCents: amountCentsToReconcile,
      reconcilableId: reconcilableId,
      reconcilableAmountCents: amountCentsToReconcile,
    }
  }
  const handleSubmitSplitAmountsModal = (items: Reconcilable[]) => {
    if (source) {
      const reconciliationData = items.map((item) => {
        const journalLineId = source?.type === 'bankAccount' ? source.id : item.id

        const reconcilableId = source?.type === 'bankAccount' ? item.id : source.id

        return {
          journalLineId,
          journalLineReconciledAmountCents: item.updatedAmountCents,
          reconcilableId,
          reconcilableAmountCents: item.updatedAmountCents,
        }
      })

      setReconciliationValues(reconciliationData)
      submitReconciliation(reconciliationData)
    }
  }

  const handleSubmitPartialModal = () => {
    submitReconciliation(reconciliationValues)
  }

  const submitReconciliation = async (values: Data[]) => {
    app.setProgress({
      message: i18n.t('finance.reconciliations.submit_messages.creating_reconciliation'),
    })

    const payload: BulkManageFinanceReconciliationReconciliationsInput[] = values.map((item) => {
      return {
        reconcilable: {
          id: item.reconcilableId,
          type: 'journal_line',
        },
        reconcilableReconciledAmountCents: item.reconcilableAmountCents,
        journalLineId: item.journalLineId,
        journalLineReconciledAmountCents: item.journalLineReconciledAmountCents,
      }
    })

    const result = await bulkManageFinanceReconciliations({
      variables: {
        companyId: company.id,
        reconciliations: payload,
        reconciliationIdsToRemove: reconciliationIdsToRemove,
      },
      update: (cache, { data }) => {
        if (reconciliationIdsToRemove.length) {
          reconciliationIdsToRemove.forEach((id) => {
            cache.evict({
              id: cache.identify({
                id: id,
                __typename: 'FinanceReconciliation',
              }),
            })
          })
        }

        if (data?.financeMutations.bulkManageFinanceReconciliation.reconciliations?.length == 0) {
          cache.evict({
            id: cache.identify({
              id: source?.id,
              __typename: 'FinanceJournalLine',
            }),
            fieldName: 'fullyReconciledAt',
          })
        }
      },
    })

    app.setProgress(null)
    setShowPartialPendingBankJournalModal(false)
    setShowPartialPendingNotBankJournalModal(false)
    setShowSplitAmountsModal(false)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.reconciliations.submit_messages.creating_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.reconciliations.submit_messages.creating_success'),
      })
      setDirtyForm(false)
      const createdReconciliations = handled.data.reconciliations || []
      const updatedJournalLine = createdReconciliations[0]?.journalLine

      setReconciliationValues([])
      setReconciliationIdsToRemove([])

      if (updatedJournalLine?.fullyReconciledAt && onJournalLineFullyReconciled) {
        onJournalLineFullyReconciled()
      }
    }
  }

  const handleReconcile = () => {
    if (!source) {
      return
    }

    if (!selectedReconciliations.length && !reconciliationIdsToRemove.length) {
      return
    }

    if (selectedReconciliations.length === 0 && reconciliationIdsToRemove.length > 0) {
      submitReconciliation([])
      return
    }

    if (initialAmountPendingToBeReconciled === amountCentsToBeReconciled) {
      // Both journal lines will be fully reconciled.
      const reconciliationData = newReconciliations.map((reconciliation) => {
        return createReconciliationData(reconciliation, getBalanceAmount(reconciliation))
      })
      submitReconciliation(reconciliationData)
    } else if (initialAmountPendingToBeReconciled > amountCentsToBeReconciled) {
      // The suspense journal line won't be fully reconciled, but the rest of journal lines will.
      const reconciliatonData = newReconciliations.map((reconciliation) => {
        return createReconciliationData(reconciliation, getBalanceAmount(reconciliation))
      })

      setReconciliationValues(reconciliatonData)
      setShowPartialPendingBankJournalModal(true)
    } else if (initialAmountPendingToBeReconciled < amountCentsToBeReconciled) {
      // The suspense journal line will be fully reconciled, but not the rest of journal lines will.
      if (newReconciliations.length > 1) {
        // We're adding many reconciliations, so the user has to split the journal line amount between them.
        setReconciliationValues([])
        setShowSplitAmountsModal(true)
      } else {
        // The suspense journal line will be fully reconciled, but the onter of journal lines won't.
        const reconciliatonData = newReconciliations.map((reconciliation) => {
          return createReconciliationData(reconciliation, initialAmountPendingToBeReconciled)
        })
        setReconciliationValues(reconciliatonData)
        setShowPartialPendingNotBankJournalModal(true)
      }
    }
  }

  return {
    handleReconcile,
    handleSubmitPartialModal,
    handleSubmitSplitAmountsModal,
    addReconciliation,
    removeReconciliation,
    isSelectedReconciliation,
    dirtyForm,
    selectedReconciliations,
    newReconciliations,
    initialAmountPendingToBeReconciled,
  }
}
